/**
 * 固件 mock 数据与版本比较（对齐 111/firmwareMockData.js）
 */

import type { FirmwareMockItem, FirmwareTab } from './firmware-types'
import { FIRMWARE_TABS, VERSION_KEY_MAP } from './firmware-types'

const MOCK_FIRMWARE_DATA: Record<FirmwareTab, FirmwareMockItem> = {
  earphone: {
    deviceName: 'Headset Main',
    latestVersion: 'V1.1.11',
    size: '796 KB',
    description: 'Headset firmware update.',
    historyList: [
      { version: 'V1.1.11', fileName: 'G1_Headset_Black_260825_V1.1.11.up', description: 'Headset firmware update.', update_date: '2026.08.25 10:13' },
      { version: 'V1.1.10', fileName: 'G1_Headset_Black_260805_V1.1.10.up', description: 'Headset firmware update.', update_date: '2026.08.05 17:37' },
      { version: 'V1.1.9', fileName: 'G1_Headset_Black_260801_V1.1.9.up', description: 'Headset firmware update.', update_date: '2026.08.01 11:14' },
      { version: 'V1.1.8', fileName: 'G1_Headset_Black_260731_V1.1.8.up', description: 'Headset firmware update.', update_date: '2026.07.31 11:14' },
    ],
  },
  earphoneBluetooth: {
    deviceName: 'Headset Bluetooth',
    latestVersion: 'V1.1.3',
    size: '777 KB',
    description: 'Improved pairing speed with multi-device support.',
    historyList: [
      { version: 'V1.1.3', fileName: 'G1_Bluetooth_260824_V1.1.3.up', description: 'Improved pairing speed with multi-device support.', update_date: '2026.08.24 10:15' },
      { version: 'V1.1.2', fileName: 'G1_Bluetooth_260714_V1.1.2.up', description: 'Enhanced Bluetooth connection stability.', update_date: '2026.07.14 11:03' },
      { version: 'V1.1.1', fileName: 'G1_Bluetooth_260714_V1.1.1.up', description: 'Initial Bluetooth firmware release.', update_date: '2026.07.14 11:03' },
    ],
  },
  earphoneNoiseCancel: {
    deviceName: 'Headset ENC',
    latestVersion: 'V1.1.2',
    size: '453 KB',
    description: 'Advanced noise cancellation logic for office environments.',
    historyList: [
      { version: 'V1.1.2', fileName: 'G1_Noise_260724_V1.1.2.up', description: 'Advanced noise cancellation logic for office environments.', update_date: '2026.07.24 21:23' },
      { version: 'V1.1.1', fileName: 'G1_Noise_260714_V1.1.1.up', description: 'Initial wind-noise reduction algorithm upgrade.', update_date: '2026.07.14 11:03' },
    ],
  },
  dongle: {
    deviceName: 'Dongle',
    latestVersion: 'V1.1.10',
    size: '496 KB',
    description: 'Fixed known stability issues on specific PC chipsets.',
    historyList: [
      { version: 'V1.1.10', fileName: 'G1_Dongle_Black_260825_V1.1.10.up', description: 'Fixed known stability issues on specific PC chipsets.', update_date: '2026.08.25 10:11' },
      { version: 'V1.1.9', fileName: 'G1_Dongle_Black_260805_V1.1.9.up', description: 'Fixed known stability issues on specific PC chipsets.', update_date: '2026.08.05 16:38' },
      { version: 'V1.1.8', fileName: 'G1_Dongle_Black_260801_V1.1.8.up', description: 'Fixed known stability issues on specific PC chipsets.', update_date: '2026.08.01 11:15' },
      { version: 'V1.1.7', fileName: 'G1_Dongle_260731_V1.1.7.up', description: 'Fixed known stability issues on specific PC chipsets.', update_date: '2026.07.31 11:15' },
    ],
  },
}

export function parseFirmwareVersion(version: string): number[] {
  if (!version) return []
  return String(version)
    .trim()
    .replace(/^[vV]/, '')
    .split('.')
    .map(n => parseInt(n, 10) || 0)
}

export function compareFirmwareVersion(v1: string, v2: string): number {
  const a = parseFirmwareVersion(v1)
  const b = parseFirmwareVersion(v2)
  const len = Math.max(a.length, b.length)
  for (let i = 0; i < len; i++) {
    const n1 = a[i] || 0
    const n2 = b[i] || 0
    if (n1 > n2) return 1
    if (n1 < n2) return -1
  }
  return 0
}

export function getFirmwareMockData(tab: FirmwareTab): FirmwareMockItem {
  return MOCK_FIRMWARE_DATA[tab] || MOCK_FIRMWARE_DATA.earphone
}

export function getFirmwareHistoryList(tab: FirmwareTab) {
  const data = getFirmwareMockData(tab)
  return Array.isArray(data.historyList) ? data.historyList : []
}

export function getDeviceCurrentVersion(
  tab: FirmwareTab,
  deviceDetail?: Record<string, unknown> | null
): string {
  const key = VERSION_KEY_MAP[tab]
  const version = deviceDetail?.[key]
  return typeof version === 'string' && version.trim() !== '' ? version.trim() : ''
}

export function hasFirmwareUpdate(tab: FirmwareTab, deviceDetail?: Record<string, unknown> | null): boolean {
  const current = getDeviceCurrentVersion(tab, deviceDetail)
  const latest = (getFirmwareMockData(tab).latestVersion || '').trim()
  return !!(current && latest && compareFirmwareVersion(latest, current) > 0)
}

export function getTabsWithFirmwareUpdate(deviceDetail?: Record<string, unknown> | null): FirmwareTab[] {
  return FIRMWARE_TABS.filter(tab => hasFirmwareUpdate(tab, deviceDetail))
}

export function hasAnyFirmwareUpdate(deviceDetail?: Record<string, unknown> | null): boolean {
  return getTabsWithFirmwareUpdate(deviceDetail).length > 0
}

export function buildFirmwareUrl(tab: FirmwareTab, version: string): string {
  const v = String(version || '').trim()
  const folder = tab ? (tab === 'earphone' ? 'Earphone' : tab === 'earphoneBluetooth' ? 'EarphoneBluetooth' : tab === 'earphoneNoiseCancel' ? 'EarphoneNoiseCancel' : 'Dongle') : ''
  if (!folder || !v) return ''
  const item = getFirmwareHistoryList(tab).find(entry => entry.version === v)
  const base = import.meta.env.BASE_URL || '/'
  return `${base}upgrading/${folder}/${item?.fileName || `${v}.up`}`
}
